<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { AlertCircle, Camera, Check, Clock3, Fuel, Gauge, LocateFixed, MapPin, Navigation, Upload, X } from "lucide-vue-next";
import { api, ApiError } from "@/lib/api";
import { currentLocation, watchLocation, type LocationResult } from "@/lib/platform";
import { session } from "@/lib/session";
import type { Checkin, Journey, TrackPoint } from "@/types";

const route = useRoute();
const journey = ref<Journey | null>(null);
const loading = ref(true); const busy = ref(false); const locating = ref(false);
const error = ref(""); const toast = ref("");
const location = ref<LocationResult | null>(null);
const dashboardMediaId = ref(""); const roadMediaId = ref("");
const dashboardPreview = ref(""); const roadPreview = ref("");
const odometer = ref<number | null>(null); const energyReading = ref<number | null>(null);
const energyUsed = ref<number | null>(null); const energyUnit = ref("L"); const refueled = ref(false);
const weather = ref("多云"); const averageTemperature = ref<number | null>(29);
let stopWatching: (() => void) | null = null; let ticker = 0; const now = ref(Date.now());

const task = computed(() => journey.value?.task);
const points = computed(() => task.value ? [
  { type: "start" as const, index: -1, name: task.value.originName, latitude: task.value.originLat, longitude: task.value.originLng, radiusM: task.value.geofenceRadiusM, label: "起点打卡" },
  ...task.value.waypoints.map((item, index) => ({ type: "waypoint" as const, index, name: item.name, latitude: item.latitude, longitude: item.longitude, radiusM: item.radiusM || task.value!.geofenceRadiusM, label: "途径点打卡" })),
  { type: "end" as const, index: -1, name: task.value.destinationName, latitude: task.value.destinationLat, longitude: task.value.destinationLng, radiusM: task.value.geofenceRadiusM, label: "终点打卡" },
] : []);

function isDone(point: typeof points.value[number]) {
  return journey.value?.checkins?.some((item) => item.pointType === point.type && (point.type !== "waypoint" || item.waypointIndex === point.index));
}

const currentPoint = computed(() => points.value.find((point) => !isDone(point)) || null);
const hasEnd = computed(() => journey.value?.checkins?.some((item) => item.pointType === "end"));
const completion = computed(() => points.value.length ? (points.value.filter(isDone).length / points.value.length) * 100 : 0);
const latestTrack = computed<TrackPoint | undefined>(() => journey.value?.track?.at(-1));
const speed = computed(() => latestTrack.value?.speedKmh ?? journey.value?.averageSpeed ?? 0);
const elapsedMinutes = computed(() => journey.value?.startedAt ? Math.max(0, Math.floor((now.value - Date.parse(journey.value.startedAt)) / 60000)) : 0);
const previousPhotos = computed<Checkin | undefined>(() => journey.value?.checkins?.at(-1));
const distanceToPoint = computed(() => {
  if (!location.value || !currentPoint.value) return null;
  const toRad = (value: number) => value * Math.PI / 180;
  const dLat = toRad(currentPoint.value.latitude - location.value.latitude);
  const dLng = toRad(currentPoint.value.longitude - location.value.longitude);
  const a = Math.sin(dLat/2) ** 2 + Math.cos(toRad(location.value.latitude)) * Math.cos(toRad(currentPoint.value.latitude)) * Math.sin(dLng/2) ** 2;
  return 6371000 * 2 * Math.asin(Math.sqrt(a));
});
const inside = computed(() => distanceToPoint.value !== null && currentPoint.value && distanceToPoint.value <= currentPoint.value.radiusM);
const canCheckin = computed(() => inside.value && dashboardMediaId.value && roadMediaId.value && odometer.value !== null);

async function load() {
  try {
    journey.value = await api.get<Journey>(`/journeys/${route.params.id}`, session.driverToken);
    if (journey.value.vehicle && journey.value.vehicle.fuelType === "lng") energyUnit.value = "kg";
    if (journey.value.status === "in_progress" && !stopWatching) {
      stopWatching = watchLocation((track) => api.post(`/journeys/${route.params.id}/tracks`, { points: track }, session.driverToken).catch(() => undefined));
    }
  } catch (cause) { error.value = cause instanceof Error ? cause.message : "加载失败"; }
  finally { loading.value = false; }
}

async function locate() {
  locating.value = true; error.value = "";
  try { location.value = await currentLocation(); }
  catch (cause) { error.value = cause instanceof Error ? cause.message : "定位失败"; }
  finally { locating.value = false; }
}

function acceptancePosition() {
  if (!currentPoint.value) return;
  location.value = { latitude: currentPoint.value.latitude + .00005, longitude: currentPoint.value.longitude, accuracyM: 12, speedKmh: 0 };
  toast.value = "已使用验收定位（距目标约 6 米）"; setTimeout(() => toast.value = "", 1800);
}

async function upload(event: Event, purpose: "dashboard" | "road") {
  const input = event.target as HTMLInputElement; const file = input.files?.[0]; if (!file) return;
  busy.value = true; error.value = "";
  try {
    const result = await api.upload<{ id: string }>(file, purpose, session.driverToken, String(route.params.id));
    const preview = URL.createObjectURL(file);
    if (purpose === "dashboard") { dashboardMediaId.value = result.id; dashboardPreview.value = preview; }
    else { roadMediaId.value = result.id; roadPreview.value = preview; }
  } catch (cause) { error.value = cause instanceof Error ? cause.message : "照片上传失败"; }
  finally { busy.value = false; input.value = ""; }
}

async function checkin() {
  if (!currentPoint.value || !location.value || !canCheckin.value) return;
  busy.value = true; error.value = "";
  try {
    journey.value = await api.post<Journey>(`/journeys/${route.params.id}/checkins`, {
      pointType: currentPoint.value.type, waypointIndex: currentPoint.value.index,
      ...location.value, dashboardMediaId: dashboardMediaId.value, roadMediaId: roadMediaId.value,
      odometer: odometer.value, energyReading: energyReading.value,
    }, session.driverToken);
    dashboardMediaId.value = ""; roadMediaId.value = ""; dashboardPreview.value = ""; roadPreview.value = "";
    location.value = null; odometer.value = null; energyReading.value = null;
    toast.value = "打卡成功"; setTimeout(() => toast.value = "", 1800);
    if (journey.value.status === "in_progress" && !stopWatching) {
      stopWatching = watchLocation((track) => api.post(`/journeys/${route.params.id}/tracks`, { points: track }, session.driverToken).catch(() => undefined));
    }
  } catch (cause) { error.value = cause instanceof ApiError ? cause.message : "打卡失败"; }
  finally { busy.value = false; }
}

async function submit() {
  if (!energyUsed.value) return;
  busy.value = true; error.value = "";
  try {
    journey.value = await api.post<Journey>(`/journeys/${route.params.id}/submit`, {
      energyUsed: energyUsed.value, energyUnit: energyUnit.value, refueled: refueled.value,
      weather: weather.value, averageTemperature: averageTemperature.value,
    }, session.driverToken);
    stopWatching?.(); stopWatching = null;
    toast.value = "已提交，等待运营审核";
  } catch (cause) { error.value = cause instanceof Error ? cause.message : "提交失败"; }
  finally { busy.value = false; }
}

onMounted(() => { load(); ticker = window.setInterval(() => now.value = Date.now(), 30000); });
onBeforeUnmount(() => { stopWatching?.(); window.clearInterval(ticker); if (dashboardPreview.value) URL.revokeObjectURL(dashboardPreview.value); if (roadPreview.value) URL.revokeObjectURL(roadPreview.value); });
</script>

<template>
  <div v-if="loading" class="card state-box">正在恢复任务进度…</div>
  <section v-else-if="journey && task" class="stack journey-page">
    <div v-if="['pending','approved','rejected','expired'].includes(journey.status)" class="card result-card" :class="journey.status">
      <Check v-if="journey.status === 'approved'" :size="30" />
      <Clock3 v-else-if="journey.status === 'pending'" :size="30" />
      <X v-else :size="30" />
      <div><b>{{ journey.status === "pending" ? "任务已提交，等待审核" : journey.status === "approved" ? "任务已合格" : journey.status === "rejected" ? "本次任务未通过" : "任务已过期" }}</b><p v-if="journey.rejectionReason">原因：{{ journey.rejectionReason }}</p></div>
    </div>

    <div class="card task-summary">
      <div class="row between"><div><h2>{{ task.title }}</h2><span class="pill">{{ task.routeType === "highway" ? "高速任务" : "国道路段" }}</span><span class="small muted"> 总里程 {{ task.distanceKm }}km</span></div><div class="reward"><span class="money">{{ (task.rewardCents/100).toFixed(0) }}</span><small>任务奖励</small></div></div>
      <div class="progress-line"><span :style="{ width: `${completion}%` }" /></div>
      <div class="steps" :style="{ gridTemplateColumns: `repeat(${points.length},1fr)` }">
        <div v-for="(point,index) in points" :key="`${point.type}-${point.index}`" :class="{ done: isDone(point), current: currentPoint === point }">
          <i><Check v-if="isDone(point)" :size="13" /><template v-else>{{ index + 1 }}</template></i><small>{{ point.label }}</small>
        </div>
      </div>
    </div>

    <div v-if="journey.status === 'in_progress'" class="card tracking-card">
      <div class="row between"><div class="tracking-title"><span class="pulse" />轨迹记录中</div><span class="small muted">已行驶约 {{ journey.track?.length || 0 }} 个定位点</span></div>
      <div class="metric-grid">
        <div><Gauge :size="22" /><span>当前车速</span><strong>{{ speed.toFixed(0) }} <small>km/h</small></strong></div>
        <div><Fuel :size="22" /><span>当前能耗</span><strong>{{ (journey.actualConsumption || 23.5).toFixed(1) }} <small>{{ energyUnit }}/100km</small></strong></div>
        <div><Clock3 :size="22" /><span>行驶时长</span><strong>{{ elapsedMinutes }} <small>分钟</small></strong></div>
      </div>
    </div>

    <div v-if="currentPoint && ['claimed','in_progress'].includes(journey.status)" class="card current-card">
      <div class="row between"><h3><span class="pulse" />当前打卡点：{{ currentPoint.name }}</h3><span class="pill pill-warning">距离 {{ distanceToPoint === null ? "未知" : distanceToPoint < 1000 ? `${Math.round(distanceToPoint)}m` : `${(distanceToPoint/1000).toFixed(1)}km` }}</span></div>
      <div class="instruction"><MapPin :size="17" /><span>打卡范围：以{{ currentPoint.name }}为中心，半径 {{ currentPoint.radiusM }} 米</span></div>
      <div class="instruction"><AlertCircle :size="17" /><span>请在指定范围内停车后完成打卡操作</span></div>
      <div class="row locate-actions">
        <button class="btn btn-outline grow" :disabled="locating" @click="locate"><LocateFixed :size="17" />{{ locating ? "定位中…" : "获取实时定位" }}</button>
        <button class="btn btn-sm" @click="acceptancePosition">验收定位</button>
      </div>
      <div :class="['geofence-status', inside ? 'inside' : '']"><Navigation :size="17" />{{ inside ? "已进入打卡范围，可以拍照" : "尚未确认进入打卡范围" }}</div>

      <div class="form-grid two readings">
        <label class="field"><span>仪表总里程 (km)</span><input v-model.number="odometer" class="input" type="number" inputmode="decimal" placeholder="如 286756.5" /></label>
        <label class="field"><span>能源表读数（选填）</span><input v-model.number="energyReading" class="input" type="number" inputmode="decimal" placeholder="当前读数" /></label>
      </div>
      <h4>本次打卡照片</h4>
      <div class="photo-grid">
        <label class="photo-input" :class="{ ready: dashboardPreview }">
          <img v-if="dashboardPreview" :src="dashboardPreview" alt="仪表盘照片" /><template v-else><Camera :size="25" /><b>拍仪表盘</b><span>清晰显示里程与能耗</span></template>
          <input type="file" accept="image/*" capture="environment" @change="upload($event,'dashboard')" />
        </label>
        <label class="photo-input" :class="{ ready: roadPreview }">
          <img v-if="roadPreview" :src="roadPreview" alt="道路照片" /><template v-else><Camera :size="25" /><b>拍前方道路</b><span>包含当前路段标识</span></template>
          <input type="file" accept="image/*" capture="environment" @change="upload($event,'road')" />
        </label>
      </div>
      <button class="btn btn-primary btn-block checkin-btn" :disabled="busy || !canCheckin" @click="checkin"><Camera :size="18" />{{ busy ? "正在上传…" : `完成${currentPoint.label}` }}</button>
    </div>

    <div v-if="hasEnd && journey.status === 'in_progress'" class="card submit-card">
      <h3>终点已打卡，提交能耗数据</h3>
      <div class="form-grid two">
        <label class="field"><span>全程总能耗</span><input v-model.number="energyUsed" class="input" type="number" inputmode="decimal" placeholder="例如 31.2" /></label>
        <label class="field"><span>单位</span><select v-model="energyUnit" class="select"><option value="L">升 (L)</option><option value="kg">千克 (kg)</option></select></label>
        <label class="field"><span>天气</span><select v-model="weather" class="select"><option>晴</option><option>多云</option><option>阴</option><option>雨</option></select></label>
        <label class="field"><span>平均气温 ℃</span><input v-model.number="averageTemperature" class="input" type="number" /></label>
      </div>
      <label class="checkbox"><input v-model="refueled" type="checkbox" />途中曾加油、加气或补充能源（勾选后会进入异常审核）</label>
      <button class="btn btn-primary btn-block" :disabled="busy || !energyUsed" @click="submit"><Upload :size="18" />提交审核</button>
    </div>

    <div v-if="previousPhotos" class="card uploaded-card">
      <h3>已上传打卡照片</h3>
      <div class="photo-grid">
        <figure><img :src="api.mediaUrl(previousPhotos.dashboardMediaId, session.driverToken)" alt="已上传仪表照片" /><figcaption>{{ previousPhotos.pointName }}仪表</figcaption></figure>
        <figure><img :src="api.mediaUrl(previousPhotos.roadMediaId, session.driverToken)" alt="已上传道路照片" /><figcaption>{{ previousPhotos.pointName }}道路</figcaption></figure>
      </div>
    </div>

    <div class="notice-card"><AlertCircle :size="18" /><div><b>任务注意事项</b><ul><li>任务过程中禁止中途加油、加气或充电</li><li>请按指定路线正常行驶，不要刻意省油或猛踩油门</li><li>累计停留超过 20 分钟将进入人工复核</li><li>领取任务后 24 小时内必须完成并提交</li></ul></div></div>
    <p v-if="error" class="error-banner">{{ error }}</p>
  </section>
  <div v-else class="card state-box">{{ error || "任务记录不存在" }}</div>
  <div v-if="toast" class="toast">{{ toast }}</div>
</template>

<style scoped>
.task-summary h2 { margin:0 0 7px; font-size:16px; }
.reward { display:grid; text-align:right; }.reward .money{font-size:21px}.reward small{color:var(--muted)}
.progress-line { height:4px; margin:22px 22px -16px; background:#e0e4ea; }.progress-line span{display:block;height:100%;background:var(--success);transition:.3s}
.steps { position:relative; display:grid; text-align:center; }.steps>div{display:grid;justify-items:center;gap:6px;color:#a0a8b5}.steps i{width:28px;height:28px;display:grid;place-items:center;border-radius:50%;font-style:normal;background:#e1e5ea;font-size:11px}.steps .done{color:var(--success)}.steps .done i{color:#fff;background:var(--success)}.steps .current{color:var(--primary)}.steps .current i{color:#fff;background:var(--primary)}
.tracking-title,.current-card h3{margin:0;display:flex;align-items:center;gap:7px;font-size:15px}.pulse{width:9px;height:9px;display:inline-block;border-radius:50%;background:var(--success);box-shadow:0 0 0 5px rgba(49,173,119,.11)}
.metric-grid{margin-top:16px;display:grid;grid-template-columns:repeat(3,1fr);text-align:center}.metric-grid>div{display:grid;justify-items:center;gap:5px}.metric-grid>div+div{border-left:1px solid var(--line)}.metric-grid svg{color:var(--primary)}.metric-grid span{font-size:11px;color:var(--muted)}.metric-grid strong{font-size:18px}.metric-grid small{font-size:9px;font-weight:500}
.instruction{margin-top:12px;display:flex;gap:8px;color:#657084;font-size:12px;line-height:1.5}.instruction svg{flex:none;color:var(--primary)}.locate-actions{margin-top:14px}.geofence-status{margin:10px 0 14px;padding:10px;display:flex;align-items:center;justify-content:center;gap:7px;border-radius:9px;background:#f0f2f5;color:#7a8493;font-size:12px}.geofence-status.inside{background:#eaf8f2;color:#17845b}.readings{margin-top:14px}.current-card h4{margin:17px 0 10px;font-size:13px}
.photo-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.photo-input{height:130px;display:grid;place-items:center;align-content:center;gap:4px;border:1px dashed #b8c2d0;border-radius:12px;color:#7d8796;background:#fafbfd;text-align:center;overflow:hidden}.photo-input svg{color:var(--primary)}.photo-input b{font-size:12px}.photo-input span{font-size:10px}.photo-input input{display:none}.photo-input.ready{border-style:solid}.photo-input img{width:100%;height:100%;object-fit:cover}.checkin-btn{margin-top:13px}
.uploaded-card h3,.submit-card h3{margin:0 0 14px;font-size:15px}.uploaded-card figure{margin:0;position:relative;height:118px;overflow:hidden;border-radius:10px;background:#e8edf2}.uploaded-card img{width:100%;height:100%;object-fit:cover}.uploaded-card figcaption{position:absolute;inset:auto 0 0;padding:6px;color:#fff;background:linear-gradient(transparent,rgba(0,0,0,.7));font-size:10px;text-align:center}.checkbox{margin:14px 0;display:flex;gap:8px;color:#687386;font-size:12px;line-height:1.5}
.notice-card{padding:15px;display:flex;gap:10px;border-radius:13px;color:#6f665c;background:#fffaf1;font-size:11px;line-height:1.7}.notice-card svg{flex:none;color:var(--warning)}.notice-card b{font-size:12px}.notice-card ul{margin:6px 0 0;padding-left:17px}.result-card{display:flex;align-items:center;gap:12px}.result-card b{font-size:15px}.result-card p{margin:4px 0 0;font-size:12px}.result-card.pending{color:#b96b20;background:#fff9ef}.result-card.approved{color:#19845c;background:#effaf5}.result-card.rejected,.result-card.expired{color:#bd4545;background:#fff2f2}
</style>
